import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, MessageSquare, ChevronRight, LogIn } from "lucide-react";
import { nexusApi, useNexusAuth } from "@/context/NexusAuthContext";
import "@/pages/nexus/nexus.css";

export default function NexusDeals() {
  const { buyer, loading } = useNexusAuth() || {};
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!buyer) return;
    nexusApi.get("/deals")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [buyer, loading]);

  if (loading) {
    return (
      <div className="nx-container py-24 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#C9896E]" size={28} />
      </div>
    );
  }

  if (!buyer) {
    return (
      <div data-testid="nexus-deals-anon" className="nx-container py-24 max-w-md mx-auto text-center">
        <LogIn className="mx-auto text-[#C9896E] mb-4" size={30} />
        <h1 className="font-serif text-3xl text-white mb-3">Sign in to view your deals</h1>
        <p className="text-white/70 mb-6">Access private deal threads with Corpora Consultants for every NDA you have accepted.</p>
        <button onClick={() => nav("/nexus/signin?next=/nexus/deals")} className="nx-btn-primary" data-testid="deals-signin-cta">
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div data-testid="nexus-deals" className="nx-container py-16">
      <div className="mb-10">
        <div className="nx-eyebrow rule mb-3">Buyer dashboard</div>
        <h1 className="font-serif text-3xl md:text-4xl text-white">Your deal threads</h1>
        <p className="text-white/60 mt-3 max-w-2xl">
          Every NDA you have accepted opens a private thread here with Corpora Consultants. All messages are logged and reviewed within one business day.
        </p>
      </div>

      {items === null ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[#C9896E]" size={22} /></div>
      ) : items.length === 0 ? (
        <div className="border border-white/10 p-10 text-center" data-testid="deals-empty">
          <MessageSquare className="mx-auto text-white/30 mb-4" size={28} />
          <div className="text-white/70 mb-4">No deals yet.</div>
          <div className="text-sm text-white/50 mb-6">Accept an NDA on any listing to open a private thread with our team.</div>
          <Link to="/nexus/listings" className="nx-btn-primary inline-flex">Browse listings</Link>
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10" data-testid="deals-list">
          {items.map((d) => (
            <Link
              key={d.id}
              to={`/nexus/deals/${d.id}`}
              className="flex items-center justify-between gap-4 px-6 py-5 hover:bg-white/[0.02] transition-colors"
              data-testid={`deal-row-${d.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <div className="text-white font-serif text-lg truncate">{d.listing_title}</div>
                  {d.unread_buyer > 0 && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] bg-[#C9896E] text-black px-2 py-0.5" data-testid={`deal-unread-${d.id}`}>
                      {d.unread_buyer} new
                    </span>
                  )}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                  {d.reference} · Last activity {new Date(d.last_message_at).toLocaleString("en-GB")}
                </div>
              </div>
              <ChevronRight size={16} className="text-white/40 shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
